import React, { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import HowToRegOutlined from '@mui/icons-material/HowToRegOutlined';
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { KebabButton } from './NotificationItem.styled';
import { CHAT_ENABLED } from '../../../constants/featureFlags';

// reg-kelly-0902-notif-duplicate-kebabs-and-option-set: a non-follow actor row
// (CLONE/SHARE/COMMENT/FAVORITE/…) used to render a SECOND, independent "..."
// overflow (the shared UserActions "compact" variant, Follow/Message) right next
// to this per-row kebab whenever the container wired delete/mark-read/report —
// two triggers on one row, each with a disjoint option set. NotificationItem now
// merges Follow/Message into THIS menu instead (only when it also passes
// actorUserId — a row with no wired kebab keeps the standalone UserActions
// affordance, unaffected). Only mounted (and only then calls the relationship
// hook / touches redux+react-query) when actorUserId is actually provided, so
// every existing caller/test that omits it renders byte-identical. useUserRelationship
// is require()'d IN-BODY (same pattern as pages/Notifications/index.jsx's lazy
// openReportForActor require) rather than a top-level import — it drags in the
// full segmentTools/Editor/tinymce module graph via redux/actions, which every
// unrelated NotificationItem render (and its tests) would otherwise pay for.
function FollowMessageMenuItems({ userId, onDone }) {
  // eslint-disable-next-line global-require
  const useUserRelationship = require('../../../hooks/useUserRelationship').default;
  const {
    isSelf,
    isFollowing,
    canMessage,
    followLoading,
    followStatusLoading,
    messageLoading,
    toggleFollow,
    openMessage,
  } = useUserRelationship(userId, { fetchStatus: true });

  if (isSelf) return null;

  const messageBlocked = !canMessage;
  const followPending = followStatusLoading;
  const followDisabled = followLoading || followPending;

  return (
    <>
      <MenuItem
        onClick={(e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          if (followDisabled) return;
          toggleFollow();
          onDone(e);
        }}
        disabled={followDisabled}
        data-testid="notif-follow"
        data-follow-status-loading={followPending ? 'true' : undefined}
      >
        <ListItemIcon>
          {followDisabled ? (
            <CircularProgress size={16} />
          ) : isFollowing ? (
            <HowToRegOutlined fontSize="small" />
          ) : (
            <PersonAddAltOutlined fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>
          {followPending ? '\u00a0' : isFollowing ? 'Following' : 'Follow'}
        </ListItemText>
      </MenuItem>
      {CHAT_ENABLED && (
        <MenuItem
          onClick={async (e) => {
            if (e && e.stopPropagation) e.stopPropagation();
            if (messageBlocked || messageLoading) return;
            /* Close the menu only AFTER openMessage settles. FollowMessageMenuItems
               lives inside the MUI Menu portal; closing the menu unmounts this
               hook instance and useUserRelationship's mounted guard drops the
               dock open on the in-flight DM create (Message appeared to no-op). */
            await openMessage();
            onDone(e);
          }}
          /* MUI `disabled` sets pointer-events:none, so hover never reaches
             the tip. Keep it visually disabled and aria-disabled instead. */
          aria-disabled={messageBlocked || messageLoading || undefined}
          data-testid="notif-message"
          sx={
            messageBlocked || messageLoading
              ? { opacity: 0.38, cursor: 'default' }
              : undefined
          }
        >
          <ListItemIcon sx={messageBlocked || messageLoading ? { opacity: 1 } : undefined}>
            {messageLoading ? <CircularProgress size={16} /> : <ChatBubbleOutlineOutlined fontSize="small" />}
          </ListItemIcon>
          <Tooltip
            title={messageBlocked ? 'Follow to Message' : ''}
            arrow
            placement="left"
            disableHoverListener={!messageBlocked}
            PopperProps={{ style: { zIndex: 10001 } }}
          >
            <span data-testid="notif-message-wrap" style={{ display: 'inline-block' }}>
              <ListItemText>Message</ListItemText>
            </span>
          </Tooltip>
        </MenuItem>
      )}
    </>
  );
}

// QA-1014 (Gordon 2026-08-24): the per-row FB-style kebab (3-dot) overflow menu.
// Pinned upper-right, revealed on hover/focus (the CSS lives on NotificationContainer
// + KebabButton). Menu contents, in order:
//   · Mark as read  — only for an UNREAD row (part 1, single mark-read)
//   · Report        — only when the row has an actor to report (part 2)
//   · Delete        — always (part 2)
// Every handler stops propagation so opening/using the menu never also activates
// the row (which would navigate away). Icons are line-art (product voice), never
// emoji, per the app icon law.
function NotificationRowMenu({
  isUnread,
  canReport,
  onMarkRead,
  onReport,
  onDelete,
  label,
  // reg-kelly-0902-notif-duplicate-kebabs-and-option-set: when the container
  // also wants Follow/Message reachable from THIS row, pass the actor id and
  // those two actions render as extra items in this same menu instead of a
  // second overflow trigger. Omitted by every caller/test that doesn't opt in.
  actorUserId,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openMenu = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setAnchorEl(null);
  };
  const run = (fn) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    closeMenu(e);
    if (fn) fn();
  };

  const forWhat = label ? ` for ${label}` : '';

  return (
    <>
      <KebabButton
        type="button"
        className="notif-row-kebab"
        aria-label={`Notification options${forWhat}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={openMenu}
        onKeyDown={(e) => e.stopPropagation()}
        data-testid="notif-kebab"
        /* This opens a menu IN PLACE — the NotificationCenter panel's
           dismiss-on-click carve-out keys off this marker so the dropdown does
           not close out from under the opening menu (capture-phase handler). */
        data-actor-actions="true"
      >
        <MoreVertIcon fontSize="small" />
      </KebabButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ dense: true }}
        /* gordon-0916-stg-notifications-delete-closes-dropdown: MUI renders this
           Menu through a PORTAL to document.body, so its items are NOT DOM
           descendants of the KebabButton above (whose data-actor-actions="true"
           is what NotificationCenter's onClickCapture dismiss handler looks for
           via e.target.closest(...)). Clicking a menu item (Delete, Mark as
           read, Report, Follow, Message) therefore failed that closest() check
           and the panel's capture-phase handler ran before this Menu's own
           stopPropagation could matter, closing the whole panel. Mark the
           rendered Paper itself so closest() finds it inside the portal's own
           subtree (a real DOM ancestor of every MenuItem here, portal boundary
           notwithstanding). */
        PaperProps={{ 'data-actor-actions': 'true' }}
      >
        {actorUserId != null && (
          <FollowMessageMenuItems userId={actorUserId} onDone={closeMenu} />
        )}
        {isUnread && (
          <MenuItem onClick={run(onMarkRead)} data-testid="notif-mark-read">
            <ListItemIcon>
              <MarkEmailReadOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as read</ListItemText>
          </MenuItem>
        )}
        {canReport && (
          <MenuItem onClick={run(onReport)} data-testid="notif-report">
            <ListItemIcon>
              <FlagOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Report</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={run(onDelete)} data-testid="notif-delete">
          <ListItemIcon>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default NotificationRowMenu;
